import { ApiError } from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ConversationTurnFilters,
  ConversationTurnsResponse,
  fetchConversationTurns,
} from '../api';

type Status = 'loading' | 'ready' | 'error' | 'too_many_openings';
interface State {
  key: string;
  status: Status;
  agent: ConversationTurnsResponse['agent'] | null;
  turns: ConversationTurnsResponse['turns'];
  window: ConversationTurnsResponse['window'] | null;
  nextCursor: string | null;
  loadingMore: boolean;
  loadMoreError: boolean;
}

// Hire's upstream read has a 10s deadline; this bounds a stalled browser connection too.
const REQUEST_TIMEOUT_MS = 30000;
const loadingState = (key: string): State => ({
  key,
  status: 'loading',
  agent: null,
  turns: [],
  window: null,
  nextCursor: null,
  loadingMore: false,
  loadMoreError: false,
});

export const useConversationTurns = (
  accountSlug: string,
  agentId: string,
  filters: ConversationTurnFilters = {},
) => {
  const filterKey = JSON.stringify(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  const stableFilters = useMemo<ConversationTurnFilters>(
    () => Object.fromEntries(JSON.parse(filterKey) as [string, string][]),
    [filterKey],
  );
  const key = JSON.stringify([accountSlug, agentId, filterKey]);
  const [state, setState] = useState(() => loadingState(key));
  const requestId = useRef(0);
  const pending = useRef<ReturnType<typeof fetchConversationTurns> | null>(
    null,
  );
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const cancel = useCallback(() => {
    requestId.current += 1;
    clearTimeout(timer.current);
    pending.current?.cancel();
    pending.current = null;
  }, []);

  const load = useCallback(
    async (page?: State) => {
      if (page && pending.current) return;
      cancel();
      requestId.current += 1;
      const id = requestId.current;
      if (page)
        setState(previous => ({
          ...previous,
          loadingMore: true,
          loadMoreError: false,
        }));
      else setState(loadingState(key));
      const request = fetchConversationTurns(accountSlug, agentId, {
        ...stableFilters,
        ...(page
          ? { ...page.window, cursor: page.nextCursor ?? undefined }
          : {}),
      });
      pending.current = request;
      timer.current = setTimeout(() => request.cancel(), REQUEST_TIMEOUT_MS);
      try {
        const response = await request;
        if (id !== requestId.current) return;
        const turns = page ? [...page.turns] : [];
        const seen = new Set(turns.map(turn => turn.id));
        response.turns.forEach(turn => {
          if (!seen.has(turn.id)) {
            seen.add(turn.id);
            turns.push(turn);
          }
        });
        setState({
          key,
          status: 'ready',
          agent: response.agent,
          turns,
          window: page?.window ?? response.window,
          nextCursor: response.next_cursor,
          loadingMore: false,
          loadMoreError: false,
        });
      } catch (error) {
        if (id !== requestId.current) return;
        const tooMany =
          error instanceof ApiError &&
          error.status === 422 &&
          (error.body as { errors?: string } | undefined)?.errors ===
            'too_many_openings';
        if (page && !tooMany) {
          setState(previous => ({
            ...previous,
            loadingMore: false,
            loadMoreError: true,
          }));
        } else {
          setState({
            ...loadingState(key),
            status: tooMany ? 'too_many_openings' : 'error',
          });
        }
      } finally {
        if (id === requestId.current) {
          clearTimeout(timer.current);
          pending.current = null;
        }
      }
    },
    [accountSlug, agentId, key, stableFilters, cancel],
  );

  useEffect(() => {
    void load();
    return cancel;
  }, [load, cancel]);

  const current = state.key === key ? state : loadingState(key);
  return {
    ...current,
    retry: () => {
      void load();
    },
    loadMore: () => {
      if (current.status === 'ready' && current.nextCursor) void load(current);
    },
  };
};
